import React, { useState, useEffect, useCallback } from 'react';
import { tableStyles } from '../ui';
import { getRateCards, updateRateCards, seedRateCards } from '../../services/adminApi';

// ====================================
// Types (mirroring backend)
// ====================================

interface RateCardRule {
  id: string;
  creativeSizePattern: string;
  lineItemKeywords: string[];
  baseRate: number;
  priority: number;
  description?: string;
}

interface TargetingSurcharge {
  id: string;
  pattern: string;
  surchargeAmount: number;
  priority: number;
  description?: string;
}

interface SpecialException {
  id: string;
  lineItemPattern: string;
  fixedRate: number;
  description?: string;
}

interface ChannelOverride {
  id: string;
  lineItemPattern: string;
  fixedChannelSplit: number;
  description?: string;
}

interface GlobalDefaults {
  defaultRate: number;
  ytSplitPercentage: number;
  channelSplitPercentage: number;
  channelSplitMinimum15s: number;
  cutAdvertiserPatterns: string[];
}

interface RateCardConfig {
  rules: RateCardRule[];
  surcharges: TargetingSurcharge[];
  exceptions: SpecialException[];
  channelOverrides: ChannelOverride[];
  globalDefaults: GlobalDefaults;
  updatedAt?: string;
  updatedBy?: string;
}

const emptyConfig: RateCardConfig = {
  rules: [],
  surcharges: [],
  exceptions: [],
  channelOverrides: [],
  globalDefaults: {
    defaultRate: 19.27,
    ytSplitPercentage: 0.45,
    channelSplitPercentage: 0.50,
    channelSplitMinimum15s: 7.00,
    cutAdvertiserPatterns: [],
  },
};

const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;

const RateCardsTab: React.FC = () => {
  const [config, setConfig] = useState<RateCardConfig>(emptyConfig);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [noConfig, setNoConfig] = useState<boolean>(false);
  const [seeding, setSeeding] = useState<boolean>(false);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoConfig(false);
    try {
      const data = await getRateCards();
      const cfg = data?.config;
      if (!cfg || (!cfg.rules && !cfg.globalDefaults)) {
        setNoConfig(true);
      } else {
        setConfig({
          rules: cfg.rules || [],
          surcharges: cfg.surcharges || [],
          exceptions: cfg.exceptions || [],
          channelOverrides: cfg.channelOverrides || [],
          globalDefaults: cfg.globalDefaults || emptyConfig.globalDefaults,
          updatedAt: cfg.updatedAt,
          updatedBy: cfg.updatedBy,
        });
      }
    } catch (err: any) {
      if (err.message?.includes('404') || err.message?.includes('not found')) {
        setNoConfig(true);
      } else {
        setError(err.message || 'Failed to load rate cards.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);
    setError(null);
    try {
      await updateRateCards(config);
      setSaveMessage('Rate cards saved successfully.');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save rate cards.');
    } finally {
      setSaving(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    setError(null);
    try {
      await seedRateCards();
      await loadConfig();
      setSaveMessage('Rate cards seeded with defaults.');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to seed rate cards.');
    } finally {
      setSeeding(false);
    }
  };

  // ====================================
  // CRUD helpers for each section
  // ====================================

  // Rules
  const addRule = () => {
    setConfig((prev) => ({
      ...prev,
      rules: [
        ...prev.rules,
        { id: generateId(), creativeSizePattern: '', lineItemKeywords: [], baseRate: 0, priority: 0, description: '' },
      ],
    }));
  };

  const updateRule = (index: number, field: keyof RateCardRule, value: any) => {
    setConfig((prev) => {
      const updated = [...prev.rules];
      if (field === 'lineItemKeywords') {
        updated[index] = { ...updated[index], [field]: (value as string).split(',').map((s: string) => s.trim()).filter(Boolean) };
      } else if (field === 'baseRate' || field === 'priority') {
        updated[index] = { ...updated[index], [field]: Number(value) || 0 };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return { ...prev, rules: updated };
    });
  };

  const removeRule = (index: number) => {
    setConfig((prev) => ({ ...prev, rules: prev.rules.filter((_, i) => i !== index) }));
  };

  // Surcharges
  const addSurcharge = () => {
    setConfig((prev) => ({
      ...prev,
      surcharges: [
        ...prev.surcharges,
        { id: generateId(), pattern: '', surchargeAmount: 0, priority: 0, description: '' },
      ],
    }));
  };

  const updateSurcharge = (index: number, field: keyof TargetingSurcharge, value: any) => {
    setConfig((prev) => {
      const updated = [...prev.surcharges];
      if (field === 'surchargeAmount' || field === 'priority') {
        updated[index] = { ...updated[index], [field]: Number(value) || 0 };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return { ...prev, surcharges: updated };
    });
  };

  const removeSurcharge = (index: number) => {
    setConfig((prev) => ({ ...prev, surcharges: prev.surcharges.filter((_, i) => i !== index) }));
  };

  // Exceptions
  const addException = () => {
    setConfig((prev) => ({
      ...prev,
      exceptions: [
        ...prev.exceptions,
        { id: generateId(), lineItemPattern: '', fixedRate: 0, description: '' },
      ],
    }));
  };

  const updateException = (index: number, field: keyof SpecialException, value: any) => {
    setConfig((prev) => {
      const updated = [...prev.exceptions];
      if (field === 'fixedRate') {
        updated[index] = { ...updated[index], [field]: Number(value) || 0 };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return { ...prev, exceptions: updated };
    });
  };

  const removeException = (index: number) => {
    setConfig((prev) => ({ ...prev, exceptions: prev.exceptions.filter((_, i) => i !== index) }));
  };

  // Channel Overrides
  const addOverride = () => {
    setConfig((prev) => ({
      ...prev,
      channelOverrides: [
        ...prev.channelOverrides,
        { id: generateId(), lineItemPattern: '', fixedChannelSplit: 0, description: '' },
      ],
    }));
  };

  const updateOverride = (index: number, field: keyof ChannelOverride, value: any) => {
    setConfig((prev) => {
      const updated = [...prev.channelOverrides];
      if (field === 'fixedChannelSplit') {
        updated[index] = { ...updated[index], [field]: Number(value) || 0 };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return { ...prev, channelOverrides: updated };
    });
  };

  const removeOverride = (index: number) => {
    setConfig((prev) => ({ ...prev, channelOverrides: prev.channelOverrides.filter((_, i) => i !== index) }));
  };

  // Global Defaults
  const updateGlobal = (field: keyof GlobalDefaults, value: any) => {
    setConfig((prev) => {
      if (field === 'cutAdvertiserPatterns') {
        return {
          ...prev,
          globalDefaults: {
            ...prev.globalDefaults,
            [field]: (value as string).split(',').map((s: string) => s.trim()).filter(Boolean),
          },
        };
      }
      return {
        ...prev,
        globalDefaults: { ...prev.globalDefaults, [field]: Number(value) || 0 },
      };
    });
  };

  if (loading) {
    return <p style={styles.loadingText}>Loading rate cards...</p>;
  }

  if (noConfig) {
    return (
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>No Rate Card Configuration Found</h2>
        <p style={styles.emptyText}>No rate card configuration exists yet. Seed it with the default values to get started.</p>
        <button
          onClick={handleSeed}
          disabled={seeding}
          style={{ ...styles.primaryButton, ...(seeding ? styles.buttonDisabled : {}) }}
        >
          {seeding ? 'Seeding...' : 'Seed Defaults'}
        </button>
        {error && <p style={styles.errorText}>{error}</p>}
      </div>
    );
  }

  return (
    <div>
      {error && <p style={styles.errorText}>{error}</p>}
      {saveMessage && <p style={styles.successText}>{saveMessage}</p>}

      {config.updatedAt && (
        <p style={styles.metaText}>
          Last updated: {new Date(config.updatedAt).toLocaleString()}
          {config.updatedBy && ` by ${config.updatedBy}`}
        </p>
      )}

      {/* Rate Card Rules */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Rate Card Rules</h2>
          <button onClick={addRule} style={styles.addButton}>+ Add Rule</button>
        </div>
        {config.rules.length === 0 ? (
          <p style={styles.emptyText}>No rules configured.</p>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Creative Size Pattern</th>
                  <th style={styles.th}>Line Item Keywords</th>
                  <th style={styles.th}>Base Rate</th>
                  <th style={styles.th}>Priority</th>
                  <th style={styles.th}>Description</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {config.rules.map((rule, i) => (
                  <tr key={rule.id} style={styles.tr}>
                    <td style={styles.td}>
                      <input
                        style={styles.cellInput}
                        value={rule.creativeSizePattern}
                        onChange={(e) => updateRule(i, 'creativeSizePattern', e.target.value)}
                        placeholder="e.g., 480 x 361v"
                      />
                    </td>
                    <td style={styles.td}>
                      <input
                        style={styles.cellInput}
                        value={rule.lineItemKeywords.join(', ')}
                        onChange={(e) => updateRule(i, 'lineItemKeywords', e.target.value)}
                        placeholder="comma-separated"
                      />
                    </td>
                    <td style={styles.td}>
                      <input
                        style={{ ...styles.cellInput, width: '80px' }}
                        type="number"
                        step="0.01"
                        value={rule.baseRate}
                        onChange={(e) => updateRule(i, 'baseRate', e.target.value)}
                      />
                    </td>
                    <td style={styles.td}>
                      <input
                        style={{ ...styles.cellInput, width: '60px' }}
                        type="number"
                        value={rule.priority}
                        onChange={(e) => updateRule(i, 'priority', e.target.value)}
                      />
                    </td>
                    <td style={styles.td}>
                      <input
                        style={styles.cellInput}
                        value={rule.description || ''}
                        onChange={(e) => updateRule(i, 'description', e.target.value)}
                        placeholder="Optional"
                      />
                    </td>
                    <td style={styles.td}>
                      <button onClick={() => removeRule(i)} style={styles.removeButton}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Targeting Surcharges */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Targeting Surcharges</h2>
          <button onClick={addSurcharge} style={styles.addButton}>+ Add Surcharge</button>
        </div>
        {config.surcharges.length === 0 ? (
          <p style={styles.emptyText}>No surcharges configured.</p>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Pattern</th>
                  <th style={styles.th}>Surcharge Amount</th>
                  <th style={styles.th}>Priority</th>
                  <th style={styles.th}>Description</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {config.surcharges.map((s, i) => (
                  <tr key={s.id} style={styles.tr}>
                    <td style={styles.td}>
                      <input
                        style={styles.cellInput}
                        value={s.pattern}
                        onChange={(e) => updateSurcharge(i, 'pattern', e.target.value)}
                        placeholder="e.g., ++++"
                      />
                    </td>
                    <td style={styles.td}>
                      <input
                        style={{ ...styles.cellInput, width: '100px' }}
                        type="number"
                        step="0.01"
                        value={s.surchargeAmount}
                        onChange={(e) => updateSurcharge(i, 'surchargeAmount', e.target.value)}
                      />
                    </td>
                    <td style={styles.td}>
                      <input
                        style={{ ...styles.cellInput, width: '60px' }}
                        type="number"
                        value={s.priority}
                        onChange={(e) => updateSurcharge(i, 'priority', e.target.value)}
                      />
                    </td>
                    <td style={styles.td}>
                      <input
                        style={styles.cellInput}
                        value={s.description || ''}
                        onChange={(e) => updateSurcharge(i, 'description', e.target.value)}
                        placeholder="Optional"
                      />
                    </td>
                    <td style={styles.td}>
                      <button onClick={() => removeSurcharge(i)} style={styles.removeButton}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Special Exceptions */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Special Exceptions</h2>
          <button onClick={addException} style={styles.addButton}>+ Add Exception</button>
        </div>
        {config.exceptions.length === 0 ? (
          <p style={styles.emptyText}>No exceptions configured.</p>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Line Item Pattern</th>
                  <th style={styles.th}>Fixed Rate</th>
                  <th style={styles.th}>Description</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {config.exceptions.map((ex, i) => (
                  <tr key={ex.id} style={styles.tr}>
                    <td style={styles.td}>
                      <input
                        style={styles.cellInput}
                        value={ex.lineItemPattern}
                        onChange={(e) => updateException(i, 'lineItemPattern', e.target.value)}
                        placeholder="e.g., IShowSpeed x MCD"
                      />
                    </td>
                    <td style={styles.td}>
                      <input
                        style={{ ...styles.cellInput, width: '80px' }}
                        type="number"
                        step="0.01"
                        value={ex.fixedRate}
                        onChange={(e) => updateException(i, 'fixedRate', e.target.value)}
                      />
                    </td>
                    <td style={styles.td}>
                      <input
                        style={styles.cellInput}
                        value={ex.description || ''}
                        onChange={(e) => updateException(i, 'description', e.target.value)}
                        placeholder="Optional"
                      />
                    </td>
                    <td style={styles.td}>
                      <button onClick={() => removeException(i)} style={styles.removeButton}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Channel Overrides */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Channel Overrides</h2>
          <button onClick={addOverride} style={styles.addButton}>+ Add Override</button>
        </div>
        {config.channelOverrides.length === 0 ? (
          <p style={styles.emptyText}>No channel overrides configured.</p>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Line Item Pattern</th>
                  <th style={styles.th}>Fixed Channel Split</th>
                  <th style={styles.th}>Description</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {config.channelOverrides.map((ov, i) => (
                  <tr key={ov.id} style={styles.tr}>
                    <td style={styles.td}>
                      <input
                        style={styles.cellInput}
                        value={ov.lineItemPattern}
                        onChange={(e) => updateOverride(i, 'lineItemPattern', e.target.value)}
                        placeholder="e.g., +sov"
                      />
                    </td>
                    <td style={styles.td}>
                      <input
                        style={{ ...styles.cellInput, width: '100px' }}
                        type="number"
                        step="0.01"
                        value={ov.fixedChannelSplit}
                        onChange={(e) => updateOverride(i, 'fixedChannelSplit', e.target.value)}
                      />
                    </td>
                    <td style={styles.td}>
                      <input
                        style={styles.cellInput}
                        value={ov.description || ''}
                        onChange={(e) => updateOverride(i, 'description', e.target.value)}
                        placeholder="Optional"
                      />
                    </td>
                    <td style={styles.td}>
                      <button onClick={() => removeOverride(i)} style={styles.removeButton}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Global Defaults */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Global Defaults</h2>
        <div style={styles.globalsGrid}>
          <div style={styles.globalField}>
            <label style={styles.label}>Default Rate</label>
            <input
              style={styles.input}
              type="number"
              step="0.01"
              value={config.globalDefaults.defaultRate}
              onChange={(e) => updateGlobal('defaultRate', e.target.value)}
            />
          </div>
          <div style={styles.globalField}>
            <label style={styles.label}>YT Split Percentage</label>
            <input
              style={styles.input}
              type="number"
              step="0.01"
              value={config.globalDefaults.ytSplitPercentage}
              onChange={(e) => updateGlobal('ytSplitPercentage', e.target.value)}
            />
            <span style={styles.hint}>e.g., 0.45 = 45%</span>
          </div>
          <div style={styles.globalField}>
            <label style={styles.label}>Channel Split Percentage</label>
            <input
              style={styles.input}
              type="number"
              step="0.01"
              value={config.globalDefaults.channelSplitPercentage}
              onChange={(e) => updateGlobal('channelSplitPercentage', e.target.value)}
            />
            <span style={styles.hint}>e.g., 0.50 = 50%</span>
          </div>
          <div style={styles.globalField}>
            <label style={styles.label}>Channel Split Minimum (15s)</label>
            <input
              style={styles.input}
              type="number"
              step="0.01"
              value={config.globalDefaults.channelSplitMinimum15s}
              onChange={(e) => updateGlobal('channelSplitMinimum15s', e.target.value)}
            />
          </div>
          <div style={{ ...styles.globalField, gridColumn: '1 / -1' }}>
            <label style={styles.label}>Cut Advertiser Patterns</label>
            <input
              style={styles.input}
              value={config.globalDefaults.cutAdvertiserPatterns.join(', ')}
              onChange={(e) => updateGlobal('cutAdvertiserPatterns', e.target.value)}
              placeholder="Comma-separated advertiser name patterns to exclude"
            />
            <span style={styles.hint}>Rows matching these advertisers will be filtered out</span>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div style={styles.bottomBar}>
        <button
          onClick={handleSeed}
          disabled={seeding}
          style={{ ...styles.secondaryButton, ...(seeding ? styles.buttonDisabled : {}) }}
        >
          {seeding ? 'Seeding...' : 'Seed Defaults'}
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{ ...styles.primaryButton, ...(saving ? styles.buttonDisabled : {}) }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  section: {
    background: 'var(--bg-surface-1)',
    borderRadius: '12px',
    border: '1px solid var(--border-1)',
    padding: '24px',
    marginBottom: '24px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  sectionTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: 'var(--fg-1)',
  },
  addButton: {
    padding: '6px 14px',
    background: 'var(--accent-soft)',
    color: 'var(--fg-1)',
    border: '1px solid var(--border-1)',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
  },
  removeButton: {
    padding: '4px 10px',
    background: 'var(--danger-soft)',
    color: 'var(--danger)',
    border: '1px solid var(--danger)',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
  },
  tableWrapper: tableStyles.wrapScroll,
  table: tableStyles.table,
  th: tableStyles.th,
  tr: {},
  td: {
    ...tableStyles.td,
    verticalAlign: 'middle' as const,
  },
  cellInput: {
    padding: '6px 8px',
    border: '1px solid var(--border-1)',
    borderRadius: '6px',
    fontSize: '13px',
    fontFamily: 'var(--font-sans)',
    color: 'var(--fg-1)',
    background: 'var(--bg-app)',
    outline: 'none',
    width: '100%',
    minWidth: '100px',
  },
  globalsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px',
  },
  globalField: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  label: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--fg-2)',
  },
  input: {
    padding: '8px 12px',
    border: '1px solid var(--border-1)',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'var(--font-sans)',
    color: 'var(--fg-1)',
    background: 'var(--bg-app)',
    outline: 'none',
  },
  hint: {
    fontSize: '11px',
    color: 'var(--fg-3)',
  },
  bottomBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 0',
  },
  primaryButton: {
    padding: '10px 24px',
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    transition: 'background 0.2s',
  },
  secondaryButton: {
    padding: '10px 24px',
    background: 'var(--bg-app)',
    color: 'var(--fg-2)',
    border: '1px solid var(--border-1)',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  loadingText: {
    color: 'var(--fg-2)',
    fontSize: '14px',
  },
  emptyText: {
    color: 'var(--fg-3)',
    fontSize: '14px',
    fontStyle: 'italic',
  },
  errorText: {
    color: 'var(--danger)',
    fontSize: '13px',
    marginBottom: '12px',
  },
  successText: {
    color: 'var(--success)',
    fontSize: '13px',
    marginBottom: '12px',
  },
  metaText: {
    fontSize: '12px',
    color: 'var(--fg-3)',
    marginBottom: '16px',
  },
};

export default RateCardsTab;
